import * as React from "react";

import { Loader } from "@progress/kendo-react-indicators";
import { getter } from "@progress/kendo-react-common";

import {
    Toolbar,
    Button,
    ButtonGroup,
} from "@progress/kendo-react-buttons";
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
    Grid,
    GridColumn,
    GridSelectionChangeEvent,
    GridCustomCellProps,
    getSelectedState,
    GridFilterCellProps,
} from "@progress/kendo-react-grid";
import { Label } from '@progress/kendo-react-labels';

import { GroupDescriptor } from '@progress/kendo-data-query';

import { ExcelExport, ExcelExportColumn, ExcelExportGroupHeaderProps } from '@progress/kendo-react-excel-export';
import { process } from "@progress/kendo-data-query";

import { useGetReportsQuery, useLazyGetReportsQuery } from '../../store/sunbright-api';

export interface InventorySummaryGridProps {
    returnLabel: string;
    onReturn?: any;
}

const DATA_ITEM_KEY = "uid";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

const initialGroup: GroupDescriptor[] = [{ field: 'region' }];

const TextOnlyGroupHeader = (props: ExcelExportGroupHeaderProps) => {
    return props.value === "<National>" ? "National" : props.value;
};

const InventorySummaryGrid = ({
    returnLabel,
    onReturn,
}: InventorySummaryGridProps) => {
    const { data: report, error: reportError, isLoading: reportLoading } = useGetReportsQuery("inventory");
    const [getReport, { data: reportLazy, error: reportErrorLazy, isLoading: reportLoadingLazy }] = useLazyGetReportsQuery();

    const [gridHeight, setGridHeight] = React.useState<string>("600px");
    const [expiryFilter, setExpiryFilter] = React.useState<string>("No Filter");
    const [excelData, setExcelData] = React.useState<any[]>([{}]);

    React.useEffect(() => {
        window.addEventListener("resize", handleResize);
    }, []);
    React.useEffect(() => {
        setGridHeight((window.innerHeight - 120) + "px");

        if (Array.isArray(report)) {
            setExcelData(process(report, { group: [{ field: "region" }] }).data)
        }
   }, [report]);

    const handleResize = () => {
        setGridHeight((window.innerHeight - 100) + "px");
    };

    const [selectedState, setSelectedState] = React.useState<{
        [id: string]: boolean | number[];
    }>({});

    const [selectedUID, setSelectedUID] = React.useState<string>("");

    const handleSelectionChange = (e: GridSelectionChangeEvent) => {
        const newState = getSelectedState({
            event: e,
            selectedState: selectedState,
            dataItemKey: DATA_ITEM_KEY,
        })
        setSelectedState(newState);

        setSelectedUID(Object.keys(newState)[0]);
    };

    const CostPerBoxCell = (props: GridCustomCellProps) => {
        const { dataItem } = props;

        if (dataItem && dataItem.costperbox !== undefined) {
            if (dataItem.costperbox === 0) {
                return <td />;
            } else {
                const formattedCost = `$${dataItem.costperbox.toFixed(2)}`;
                return <td {...props.tdProps}>{formattedCost}</td>;
            }
        }
        else {
            return <td/>
        }
    };

    const CostCell = (props: GridCustomCellProps) => {
        const { dataItem } = props;

        if (dataItem && dataItem.cost !== undefined) {
            if (dataItem.cost === 0) {
                return <td />;
            } else {
                const formattedCost = `$${dataItem.cost.toFixed(2)}`;
                return <td {...props.tdProps}>{formattedCost}</td>;
            }
        }
        else {
            return <td />
        }
    };

    const handleExpiryFilterChange = (event: DropDownListChangeEvent) => {
        console.info("value", event.value);
        if (event.value === "Not Expired") {
            getReport("inventory?expiry=1");
        } else if (event.value === "Expired")
            getReport("inventory?expiry=2");
        else {
            getReport("inventory");
        }
        setExpiryFilter(event.value);
    };

    const ExpiryFilterCell: any = (props: GridFilterCellProps) => (
        <DropDownList
            data={["No Filter", "Not Expired", "Expired"]}
            defaultValue={expiryFilter}
            onChange={handleExpiryFilterChange }
        />
    );

    var _export = React.useRef<ExcelExport | null>(null);
    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save();
        }
    };

    return (
        <React.Fragment>
            <div>
                <Toolbar>
                    <Button
                        className="sds-buttoninbar"
                        icon="undo"
                        onClick={(e) => onReturn()}
                    >
                        {returnLabel}
                    </Button>
                    <div style={{ fontSize: "large", fontWeight: "bold", paddingLeft: "64px" }}>
                        Report
                    </div>
                </Toolbar>
                <div style={{ position: "fixed", top: "46px", right: "64px", zIndex: "99" }}>
                    <ButtonGroup>
                        <Button
                            themeColor={"primary"}
                            icon="file-report"
                            style={{ borderLeftColor: "white", borderLeftWidth: "1px" }}
                            onClick={excelExport}
                        >
                            Export to Excel
                        </Button>
                    </ButtonGroup>
                </div>

                {reportError ? (<>Not Authorized</>) : reportLoading ? <Loader size="large" type="converging-spinner" /> : (report || reportLazy) ?
                    (<div>
                        <ExcelExport
                            data={excelData}
                            fileName="Inventory.xlsx"
                            group={[{ field: 'region' }]}
                            collapsible={true}
                            ref={_export}
                        >
                            <ExcelExportColumn field="cdmvcode" title="CDMV Code" width={96} />
                            <ExcelExportColumn field="supplier" title="Supplier" width={201} />
                            <ExcelExportColumn field="description" title="Description" width={475} />
                            <ExcelExportColumn field="expirytime" title="Expiry" width={136} />
                            <ExcelExportColumn field="unitsperbox" title="Units Per Box" width={96} />
                            <ExcelExportColumn field="boxes" title="Boxes" width={64} />
                            <ExcelExportColumn field="units" title="Units" width={64} />
                            <ExcelExportColumn field="costperbox" title="Cost per box" cellOptions={{ format: "$#,##0.00", textAlign: "right" }}/>
                            <ExcelExportColumn field="cost" title="Cost" cellOptions={{format: "$#,##0.00", textAlign: "right"}} width={120} />
                            <ExcelExportColumn field="region" hidden={true} groupHeader={TextOnlyGroupHeader} />
                        </ExcelExport>

                        <Grid
                            style={{ height: gridHeight }}
                            className="eventSummaryGrid"
                            data={(reportLazy ? reportLazy : report).map((item: any) => ({
                                ...item,
                                [SELECTED_FIELD]: selectedState[idGetter(item)]
                            }))}

                            autoProcessData={true}
                            groupable={true}
                            defaultGroup={initialGroup}
                            filterable={true}
                            resizable={true}
                            size={"small"}
                            dataItemKey={DATA_ITEM_KEY}
                            selectedField={SELECTED_FIELD}
                            selectable={{
                                enabled: true,
                                mode: "single",
                            }}
                            onSelectionChange={handleSelectionChange}
                        >
                            <GridColumn field="region" title="Region" filterable={false} width="0px"/>
                            <GridColumn field="type" title=" " filterable={false} width="80px" />

                            <GridColumn field="cdmvcode" title="CDMV Code" filterable={false} width="96px" />
                            <GridColumn field="supplier" title="Supplier" filterable={false} width="201px" />
                            <GridColumn field="description" title="Description" filterable={false} width="475px" />
                            <GridColumn field="expirytime" title="Expiry" format="{0:d}" width="136px" filterable={true} filterCell={ExpiryFilterCell} />
                            <GridColumn field="unitsperbox" title="Units Per Box" filterable={false} width="96px" />
                            <GridColumn field="boxes" title="Boxes" filterable={false} width="64px" />
                            <GridColumn field="units" title="Units" filterable={false} width="64px" />
                            <GridColumn field="costperbox" title="Cost per box" cells={{ data: CostPerBoxCell }} filterable={false} width="96px" />
                            <GridColumn field="cost" title="Cost" cells={{ data: CostCell }} filterable={false} width="120px" />
                        </Grid></div>) : null}
                </div>
        </React.Fragment>
    );
}
export default InventorySummaryGrid;